/* iter55f72 — Novel Framework Builder.
 *
 * A wizard-style intake that captures everything Claude needs to
 * draft a coherent, character-driven manuscript. Sections:
 *
 *   1. Basics        — title, genre, logline, chapter-count slider
 *   2. Story Bible   — POV/tense/voice + world/themes/tone + toggles
 *                       (prologue, epilogue, "The End", romance thread,
 *                        dual timeline, unreliable narrator, dark themes)
 *                       plus a paste-in field for importing an existing bible
 *   3. Characters    — chip-selectable roles + editable cards
 *                       (want/need/flaw/arc/description)
 *   4. Chapters      — per-chapter card with title, act badge, POV,
 *                       setting, conflict, revelation, and a 150-500 word
 *                       transition textarea. Word-count validator + inline
 *                       "Ask Claude to critique this chapter" button.
 *   5. Audit         — final "Beginning · Middle · End" audit + numbered
 *                       next-steps roadmap.
 *   6. Import        — creates a The Loom project, seeds everything,
 *                       auto-drafts chapters 1-3 in the background.
 *
 * Saves are debounced and per-section — the wizard patches the
 * framework doc as the writer moves between sections rather than
 * lumping everything into one giant final save.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, ChevronRight, ChevronLeft, Plus, Trash2, Wand2,
  BookOpen, Users, ListOrdered, ClipboardCheck, Rocket, FileText,
  Menu, Download, Upload, HelpCircle, Sliders, X, MapPin, Zap,
} from 'lucide-react';
import { useAuth } from '@/lib/auth_context';
import { frameworkApi, chaptersApi } from '@/lib/api';
import { streamSSE } from '@/lib/auth_api';
import { ATTRIBUTE_SCHEMA, FORMAT_SPEC_V1 } from './frameworkSchema';
import { runTour } from '@/lib/tours';
import PromptAuditDrawer from '@/components/dialogs/PromptAuditDrawer';  // iter-audit1
import PromptQualityChecklist from '@/components/PromptQualityChecklist';   // iter-studio-dashboard
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

const API   = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// iter-beta1 — FastAPI/Pydantic returns validation errors as an ARRAY of
// objects ({type, loc, msg, input, ctx, url}) under `detail`, not a
// string. Passing that array straight into `toast.error()` makes React
// try to render raw objects as children, which crashes the whole page
// (blank screen) instead of just showing a toast. Every error toast in
// this file must route through this helper.
function apiErrorMessage(e, fallback) {
  const raw = e?.response?.data?.detail;
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw) && raw.length) {
    return raw
      .map((row) => (row?.msg ? `${(row.loc?.slice(-1)?.[0] || 'field')}: ${row.msg}` : String(row)))
      .join(' · ');
  }
  if (e?.message) return e.message;
  return fallback;
}

const P = {
  paper:      '#F7F4EE',
  paperDeep:  '#EFEAE0',
  ink:        '#24201B',
  inkSoft:    '#5B5348',
  oxblood:    '#6A2E2E',
  oxbloodHi:  '#8A3A3A',
  gold:       '#C8A96E',
  goldDeep:   '#A98A4E',
  leather:    '#3A2418',
  verdant:    '#2D4A3E',
};

const ROLE_OPTIONS = ['Protagonist', 'Antagonist', 'Mentor', 'Foil', 'Love Interest', 'Sidekick', 'Supporting'];
const POV_OPTIONS   = ['first', 'third-limited', 'third-omniscient', 'second'];
const TENSE_OPTIONS = ['past', 'present'];
const VOICE_OPTIONS = ['literary', 'genre', 'YA', 'commercial', 'poetic', 'stark'];

const SECTIONS = [
  { key: 'basics',     label: 'Basics',      icon: FileText },
  { key: 'bible',      label: 'Story Bible', icon: BookOpen },
  { key: 'attributes', label: 'Attributes',  icon: Sliders },
  { key: 'characters', label: 'Characters',  icon: Users },
  { key: 'locations',  label: 'Locations',   icon: MapPin },
  { key: 'chapters',   label: 'Chapters',    icon: ListOrdered },
  { key: 'audit',      label: 'Audit',       icon: ClipboardCheck },
  { key: 'import',     label: 'Import',      icon: Rocket },
];

export default function NovelFrameworkPage() {
  const { user, checking: authLoading } = useAuth();
  const authed = !!user;
  const { fid } = useParams();
  const nav = useNavigate();
  const [framework, setFramework] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [section, setSection]   = useState('basics');
  const [saving, setSaving]     = useState(false);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  /* Load or (if no fid) show the list/new-form. */
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (fid) {
          const r = await axios.get(`${API}/framework/${fid}`);
          if (!cancelled) { setFramework(r.data); lastSavedRef.current = JSON.stringify(r.data); }
        } else {
          setFramework(null);
        }
      } catch (e) {
        toast.error('Could not load framework.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authed, fid]);

  /* Debounced auto-save when framework changes. */
  const scheduleSave = useCallback((next) => {
    if (!next?.id) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      const snapshot = JSON.stringify(next);
      if (snapshot === lastSavedRef.current) return;
      setSaving(true);
      try {
        const patch = {
          name: next.name, genre: next.genre, logline: next.logline,
          target_words: next.target_words, chapter_count: next.chapter_count,
          structure: next.structure,
          prompt_caching: next.prompt_caching,
          bible: next.bible, toggles: next.toggles,
          characters: next.characters, locations: next.locations, chapters: next.chapters,
          // iter55g19 — Persist analyzer output & project link so Dismiss
          // and manuscript-based patches round-trip across reloads.
          analysis: next.analysis,
          imported_project_id: next.imported_project_id,
        };
        const r = await axios.patch(`${API}/framework/${next.id}`, patch);
        lastSavedRef.current = JSON.stringify(r.data);
        setFramework(r.data);
      } catch (e) {
        toast.error(apiErrorMessage(e, 'Save failed.'));
      } finally { setSaving(false); }
    }, 700);
  }, []);

  const updateFramework = useCallback((updater) => {
    setFramework((cur) => {
      const next = typeof updater === 'function' ? updater(cur) : updater;
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  if (authLoading) return null;
  if (!authed) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: P.paper }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: P.oxblood }} />
      </div>
    );
  }

  if (!fid) return <FrameworkList onOpen={(id) => nav(`/framework/${id}`)} />;
  if (!framework) return null;

  return (
    <div className="flex h-screen flex-col" style={{ backgroundColor: P.paper, color: P.ink, fontFamily: 'EB Garamond, Georgia, serif' }}>
      <Header
        framework={framework}
        saving={saving}
        onBack={() => nav('/framework')}
        onOpenWorkspace={() => nav('/app/')}
        onImported={(pid) => nav(`/framework/${pid}`)}
      />

      {/* iter55g9 — Global CSS forces `overflow:hidden` on the root chain
           so the writing-suite editor can drive its own paneled scrolling.
           The Framework page has a single long content column, so it needs
           an explicit scroll container. Without this, tall sections (Basics
           past the chapter-count slider, or the AI-Draft accordion) get
           clipped and force writers to zoom out to see them. */}
      <div className="flex-1 overflow-y-auto" data-testid="nf-scroll-container">
        <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6">
          <aside className="hidden w-56 shrink-0 sm:block" data-testid="nf-nav">
          <ol className="space-y-1">
            {SECTIONS.map((s) => (
              <li key={s.key}>
                <button
                  onClick={() => setSection(s.key)}
                  data-testid={`nf-nav-${s.key}`}
                  className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-[13px] transition"
                  style={{
                    backgroundColor: section === s.key ? `${P.oxblood}18` : 'transparent',
                    color: section === s.key ? P.oxblood : P.inkSoft,
                    fontWeight: section === s.key ? 700 : 500,
                    borderLeft: section === s.key ? `3px solid ${P.oxblood}` : '3px solid transparent',
                  }}
                >
                  <s.icon className="h-4 w-4" />
                  {s.label}
                </button>
              </li>
            ))}
          </ol>
        </aside>

        <main className="flex-1 min-w-0">
          {/* iter55g18 — When the framework is linked to a project with
           *  drafted chapters, offer a one-click "Generate framework from
           *  manuscript" that fills tone/POV/logline/etc. from Claude's
           *  reading of the actual prose. */}
          {framework.imported_project_id && (
            <GenerateFrameworkBanner
              framework={framework}
              onGenerated={(patched) => setFramework(patched)}
            />
          )}
          {section === 'basics'     && <SectionBasics     f={framework} update={updateFramework} />}
          {section === 'bible'      && <SectionBible      f={framework} update={updateFramework} />}
          {section === 'attributes' && <SectionAttributes f={framework} update={updateFramework} />}
          {section === 'characters' && <SectionCharacters f={framework} update={updateFramework} />}
          {section === 'locations'  && <SectionLocations  f={framework} update={updateFramework} />}
          {section === 'chapters'   && <SectionChapters   f={framework} update={updateFramework} />}
          {section === 'audit'      && <SectionAudit      f={framework} />}
          {section === 'import'     && <SectionImport     f={framework} onImported={(pid) => nav(`/app/`)} />}

          <div className="mt-8 flex items-center justify-between border-t pt-4" style={{ borderColor: `${P.gold}55` }}>
            <button
              onClick={() => {
                const i = SECTIONS.findIndex((s) => s.key === section);
                if (i > 0) setSection(SECTIONS[i - 1].key);
              }}
              disabled={section === SECTIONS[0].key}
              data-testid="nf-prev"
              className="inline-flex items-center gap-1 rounded-sm px-3 py-2 text-[13px] font-semibold disabled:opacity-40"
              style={{ color: P.inkSoft }}
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <button
              onClick={() => {
                const i = SECTIONS.findIndex((s) => s.key === section);
                if (i < SECTIONS.length - 1) setSection(SECTIONS[i + 1].key);
              }}
              disabled={section === SECTIONS[SECTIONS.length - 1].key}
              data-testid="nf-next"
              className="inline-flex items-center gap-1 rounded-sm px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40"
              style={{ backgroundColor: P.oxblood }}
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  iter55g18 — Generate framework from manuscript banner
 * ============================================================ */
function GenerateFrameworkBanner({ framework, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/framework/${framework.id}/generate-from-manuscript`);
      onGenerated?.(r.data);
      setPreview(r.data?.analysis || null);
      toast.success('Framework updated from your manuscript.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Analysis failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 flex flex-col gap-2 rounded-sm border p-3"
      style={{
        backgroundColor: `${P.gold}18`, borderColor: `${P.gold}88`, color: P.ink,
      }}
      data-testid="nf-generate-from-manuscript-banner"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4" style={{ color: P.oxblood }} />
        <div className="flex-1 text-[13px]">
          <b>Generate framework from your manuscript.</b>{' '}
          Claude reads every chapter you've drafted, extracts tone / POV /
          cast / chapter transitions / character arcs, and fills the
          empty fields here without touching what you've already written.
        </div>
        <button
          onClick={run}
          disabled={busy}
          data-testid="nf-generate-from-manuscript-btn"
          className="inline-flex items-center gap-1 rounded-sm px-3 py-1.5 text-[12px] font-semibold text-white transition disabled:opacity-60"
          style={{ backgroundColor: P.oxblood }}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {busy ? 'Analyzing…' : 'Generate framework'}
        </button>
      </div>
      {preview && (
        <details className="text-[11.5px]" data-testid="nf-generate-preview">
          <summary className="cursor-pointer font-semibold">
            What Claude inferred (click to expand)
          </summary>
          <div className="mt-1 space-y-1 pl-3">
            {preview.voice_profile && (
              <div>
                <b>Voice</b> · {[
                  preview.voice_profile.tone, preview.voice_profile.pov,
                  preview.voice_profile.tense, preview.voice_profile.voice,
                ].filter(Boolean).join(' · ')}
              </div>
            )}
            {preview.voice_profile?.setting && (
              <div><b>Setting</b> · {preview.voice_profile.setting}</div>
            )}
            {(preview.characters || []).length > 0 && (
              <div>
                <b>Cast</b> · {preview.characters.slice(0, 12).map((c) => c.name).filter(Boolean).join(', ')}
              </div>
            )}
            {preview.three_act_synopsis?.act_1 && (
              <div className="italic">
                <b>Arc</b> · {preview.three_act_synopsis.act_1}
              </div>
            )}
            {(preview.chapter_transitions || []).length > 0 && (
              <div>
                <b>Transitions</b> ·{' '}
                {preview.chapter_transitions.slice(0, 5).map((t) => `Ch${t.from}→${t.to}`).join(', ')}
                {preview.chapter_transitions.length > 5 ? '…' : ''}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}


/* ============================================================
 *  Header
 * ============================================================ */
function Header({ framework, saving, onBack, onOpenWorkspace, onImported }) {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [helpOpen, setHelpOpen]     = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();

  const exportTemplate = async () => {
    setMenuOpen(false);
    try {
      const r = await axios.get(`${API}/framework/${framework.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(framework.name || 'framework').replace(/\s+/g, '-').toLowerCase()}.novel-framework.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded.');
    } catch (e) { toast.error(apiErrorMessage(e, 'Export failed.')); }
  };

  const createTemplate = async () => {
    setMenuOpen(false);
    // A "create as template" is a duplicate — export + re-import as a
    // new framework with a suffixed name. Keeps the round-trip logic
    // in one place instead of a separate copy endpoint.
    try {
      const exp = await axios.get(`${API}/framework/${framework.id}/export`);
      const r = await axios.post(`${API}/framework/import`, {
        template: exp.data,
        name: `${framework.name} (Template)`,
      });
      toast.success('Template created as a new framework.');
      if (onImported) onImported(r.data.id);
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not create template.')); }
  };

  return (
    <header
      className="border-b px-4 py-3"
      style={{
        borderColor: `${P.gold}66`,
        background: `linear-gradient(180deg, ${P.paperDeep} 0%, ${P.paper} 100%)`,
      }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="nf-back"
            className="text-[11px] uppercase tracking-[0.24em]"
            style={{ color: P.inkSoft }}
          >
            ← All frameworks
          </button>
          <div className="text-[15px] font-semibold" style={{ color: P.ink, fontFamily: 'Cormorant Garamond, serif' }}>
            {framework?.name || 'Novel Framework'}
          </div>
          <span className="text-[10.5px] uppercase tracking-[0.22em]" style={{ color: P.oxblood }}>
            {framework?.chapter_count} chapters
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* iter55f75 — File menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="nf-file-menu"
              className="inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] font-semibold"
              style={{ borderColor: `${P.gold}88`, color: P.ink }}
            >
              <Menu className="h-3 w-3" /> File
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full z-20 mt-1 w-56 rounded-sm border p-1 shadow-lg"
                style={{ borderColor: `${P.gold}88`, backgroundColor: P.paper }}
                data-testid="nf-file-menu-dropdown"
              >
                <MenuItem onClick={createTemplate} icon={Plus}    label="Create as Template" testid="nf-menu-create-tpl" />
                <MenuItem onClick={() => { setMenuOpen(false); setImportOpen(true); }} icon={Upload} label="Load a Template…" testid="nf-menu-load-tpl" />
                <MenuItem onClick={exportTemplate} icon={Download} label="Export as Template" testid="nf-menu-export-tpl" />
                <div className="my-1 border-t" style={{ borderColor: `${P.gold}55` }} />
                <MenuItem onClick={() => { setMenuOpen(false); setHelpOpen(true); }} icon={HelpCircle} label="Attribute Help…" testid="nf-menu-help" />
              </div>
            )}
          </div>
          {saving
            ? <span className="text-[11px]" style={{ color: P.inkSoft }}>Saving…</span>
            : <span className="text-[11px]" style={{ color: P.verdant }}>Saved ✓</span>}
          <button
            onClick={onOpenWorkspace}
            data-testid="nf-open-workspace"
            className="rounded-sm border px-2 py-1 text-[11px] font-semibold"
            style={{ borderColor: `${P.oxblood}88`, color: P.oxblood }}
          >
            Open The Loom
          </button>
        </div>
      </div>

      {helpOpen   && <HelpDialog   onClose={() => setHelpOpen(false)} />}
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onImported={(id) => { setImportOpen(false); nav(`/framework/${id}`); }} />}
    </header>
  );
}

function MenuItem({ onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px] transition hover:bg-primary/10"
      style={{ color: P.ink }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color: P.oxblood }} />
      {label}
    </button>
  );
}

/* ============================================================
 *  Help Dialog — attribute schema + format spec
 * ============================================================ */
function HelpDialog({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-auto p-6"
      style={{ backgroundColor: 'rgba(42, 31, 24, 0.55)' }}
      onClick={onClose}
      data-testid="nf-help-dialog"
    >
      <div
        className="relative mt-6 w-full max-w-3xl rounded-md border p-6 shadow-2xl"
        style={{ borderColor: P.gold, backgroundColor: P.paper }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="nf-help-close"
          className="absolute right-3 top-3 rounded-full p-1"
          style={{ color: P.inkSoft }}
          aria-label="Close help"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-[22px] font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
          Attribute Help
        </h2>
        <p className="mt-1 text-[12.5px]" style={{ color: P.inkSoft }}>
          Every attribute in the Framework Builder, what it means, and (where applicable) its valid values. This is also the template format Claude reads on import.
        </p>

        <div className="mt-4 space-y-4">
          {ATTRIBUTE_SCHEMA.map((g) => (
            <section key={g.group}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
                {g.group}
              </div>
              <dl className="mt-2 space-y-2">
                {g.attributes.map((a) => (
                  <div key={a.key} className="rounded-sm border p-2" style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}>
                    <dt className="flex items-baseline gap-2">
                      <span className="text-[12.5px] font-semibold" style={{ color: P.ink }}>{a.label}</span>
                      <code className="text-[10.5px]" style={{ color: P.inkSoft }}>{a.key}</code>
                    </dt>
                    <dd className="mt-0.5 text-[12px]" style={{ color: P.inkSoft }}>
                      {a.help}
                      {a.options && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {a.options.map((opt) => (
                            <span
                              key={String(opt)}
                              className="rounded-full px-1.5 py-0.5 text-[10px]"
                              style={{ backgroundColor: `${P.oxblood}18`, color: P.oxblood }}
                            >
                              {String(opt)}
                            </span>
                          ))}
                        </div>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
          <section>
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
              Template Format (v1)
            </div>
            <pre
              className="mt-2 whitespace-pre-wrap rounded-sm border p-3 text-[11px] leading-relaxed"
              style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep, color: P.ink,
                        fontFamily: '"SF Mono", Menlo, monospace' }}
              data-testid="nf-help-format-spec"
            >
              {FORMAT_SPEC_V1}
            </pre>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Import Template Dialog
 * ============================================================ */
function ImportDialog({ onClose, onImported }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const t = await file.text();
    setText(t);
  };

  const run = async () => {
    let parsed;
    try { parsed = JSON.parse(text); }
    catch { toast.error('Not valid JSON.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/framework/import`, { template: parsed });
      toast.success('Template imported.');
      onImported(r.data.id);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Import failed.'));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-6"
      style={{ backgroundColor: 'rgba(42, 31, 24, 0.55)' }}
      onClick={onClose}
      data-testid="nf-import-dialog"
    >
      <div
        className="relative w-full max-w-xl rounded-md border p-6 shadow-2xl"
        style={{ borderColor: P.gold, backgroundColor: P.paper }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="nf-import-close"
          className="absolute right-3 top-3 rounded-full p-1"
          style={{ color: P.inkSoft }}
          aria-label="Close import"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-[20px] font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
          Load a Template
        </h2>
        <p className="mt-1 text-[12.5px]" style={{ color: P.inkSoft }}>
          Upload a `.novel-framework.json` file, or paste the JSON directly. A fresh framework will be created — the current one is untouched.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          onChange={onFile}
          data-testid="nf-import-file"
          className="mt-3 block w-full text-[12px]"
        />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="… or paste template JSON here"
          data-testid="nf-import-textarea"
          className="mt-3 w-full resize-y rounded-sm border p-2 text-[11.5px] outline-none"
          style={{ borderColor: `${P.goldDeep}66`, color: P.ink, fontFamily: '"SF Mono", Menlo, monospace' }}
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="nf-import-cancel"
            className="rounded-sm px-3 py-2 text-[13px] font-semibold"
            style={{ color: P.inkSoft }}
          >
            Cancel
          </button>
          <button
            onClick={run}
            disabled={!text.trim() || busy}
            data-testid="nf-import-submit"
            className="inline-flex items-center gap-2 rounded-sm px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: P.oxblood }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Section — Attributes (dropdown / checkbox / toggle grid)
 * ============================================================ */
function SectionAttributes({ f, update }) {
  const b = f.bible || {};
  const t = f.toggles || {};
  const setBible  = (k, v) => update((cur) => ({ ...cur, bible:   { ...(cur.bible   || {}), [k]: v } }));
  const setToggle = (k, v) => update((cur) => ({ ...cur, toggles: { ...(cur.toggles || {}), [k]: v } }));

  return (
    <SectionCard
      title="Attributes"
      subtitle="Pick from curated options rather than free-typing. Every attribute here is documented under File → Attribute Help."
    >
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
          Voice
        </div>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {ATTRIBUTE_SCHEMA.find((g) => g.group === 'Story Bible').attributes
            .filter((a) => a.options && ['bible.pov', 'bible.tense', 'bible.voice'].includes(a.key))
            .map((a) => {
              const key = a.key.split('.')[1];
              return (
                <Field key={a.key} label={a.label} hint={a.help}>
                  <select
                    value={b[key] || a.options[0]}
                    onChange={(e) => setBible(key, e.target.value)}
                    data-testid={`nf-attr-${key}`}
                    className="mt-1 w-full rounded-sm border p-2 text-[13px]"
                    style={inputStyle()}
                  >
                    {a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              );
            })}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
          Story toggles (check any that apply)
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {ATTRIBUTE_SCHEMA.find((g) => g.group === 'Toggles').attributes.map((a) => {
            const key = a.key.split('.')[1];
            return (
              <label
                key={a.key}
                className="flex items-start gap-2 rounded-sm border p-2 text-[12.5px]"
                style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep, color: P.ink }}
              >
                <input
                  type="checkbox"
                  checked={!!t[key]}
                  onChange={(e) => setToggle(key, e.target.checked)}
                  data-testid={`nf-attr-toggle-${key}`}
                  className="mt-0.5"
                  style={{ accentColor: P.oxblood }}
                />
                <span>
                  <div className="font-semibold" style={{ color: P.ink }}>{a.label}</div>
                  <div className="text-[11px]" style={{ color: P.inkSoft }}>{a.help}</div>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-4 rounded-sm border p-3 text-[12px]"
           style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep, color: P.inkSoft }}>
        <span className="font-semibold" style={{ color: P.oxblood }}>Tip: </span>
        Free-text fields (world, themes, tone, character want/need/flaw) live under Story Bible and Characters. This page is only for curated, enumerated attributes.
      </div>
    </SectionCard>
  );
}

/* ============================================================
 *  Framework list / new form
 * ============================================================ */
function FrameworkList({ onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [name, setName]           = useState('');
  const [genre, setGenre]         = useState('romance');
  const [logline, setLogline]     = useState('');
  const [count, setCount]         = useState(12);
  const [creating, setCreating]   = useState(false);
  const [materialisingId, setMaterialisingId] = useState(null);
  // iter55g10 — "Draft a logline for me" dialog state.
  const [draftOpen,     setDraftOpen]     = useState(false);
  const [draftLoading,  setDraftLoading]  = useState(false);
  const [draftSeed,     setDraftSeed]     = useState('');
  const [draftCandidates, setDraftCandidates] = useState([]);   // string[]

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listR, galleryR] = await Promise.all([
        axios.get(`${API}/framework`),
        axios.get(`${API}/framework-gallery`),
      ]);
      setItems(listR.data.frameworks || []);
      setGallery(galleryR.data.templates || []);
    } catch {
      // ignore — still show the create form
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // iter55g10 — Fetch 3 AI-drafted logline candidates. Called both when
  // the dialog opens and when the writer hits "Redraft" for new picks.
  const runDraft = useCallback(async (seedOverride) => {
    setDraftLoading(true);
    try {
      const r = await frameworkApi.draftLoglines({
        genre: (genre || '').trim() || 'literary fiction',
        seed:  (seedOverride ?? draftSeed).trim(),
      });
      setDraftCandidates(Array.isArray(r.loglines) ? r.loglines : []);
      if (r.llm_error) {
        toast.error('Claude fell back to boilerplate — try again in a moment.', {
          description: r.llm_error,
        });
      }
    } catch (e) {
      toast.error('Could not draft loglines.', {
        description: apiErrorMessage(e, 'Try a shorter seed or check the network.'),
      });
    } finally {
      setDraftLoading(false);
    }
  }, [genre, draftSeed]);

  useEffect(() => {
    if (draftOpen && draftCandidates.length === 0 && !draftLoading) {
      runDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftOpen]);

  const pickDraftCandidate = (text) => {
    setLogline(text);
    setDraftOpen(false);
    setDraftCandidates([]);
    setDraftSeed('');
    toast.success('Logline picked — edit as needed then click Start Framework.');
  };

  const create = async () => {
    if (creating) return;
    if (!name.trim()) {
      toast.error('Please enter a working title for your framework.');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/framework`, {
        name: name.trim(),
        genre: (genre || '').trim() || 'romance',
        logline: (logline || '').trim(),
        chapter_count: Math.max(3, Math.min(60, Number.parseInt(count, 10) || 12)),
      });
      if (!r?.data?.id) {
        toast.error('Framework created but no id returned — please refresh.');
        return;
      }
      toast.success(`"${r.data.name}" created — opening the wizard.`);
      onOpen(r.data.id);
    } catch (e) {
      // iter55f99 — Pydantic returns validation errors as an array under
      // `detail`; passing that straight to sonner rendered "[object
      // Object]" and made the button feel broken. Normalise the shape.
      toast.error(apiErrorMessage(e, 'Could not create framework.'));
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this framework? Any imported project is preserved.')) return;
    try {
      await axios.delete(`${API}/framework/${id}`);
      refresh();
    } catch { toast.error('Could not delete framework.'); }
  };

  /* iter55f77 — One-click materialise a gallery template into a
   * user-owned framework and hop straight into the wizard. */
  const useTemplate = async (gid) => {
    if (materialisingId) return;
    setMaterialisingId(gid);
    try {
      const r = await axios.post(`${API}/framework-gallery/${gid}`, {});
      toast.success('Template loaded — customise before importing to The Loom.');
      onOpen(r.data.id);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not load template.'));
    } finally { setMaterialisingId(null); }
  };

  return (
    // iter55g9 — Same scroll fix as the editor view. See note above.
    <div className="h-screen overflow-y-auto" style={{ backgroundColor: P.paper, color: P.ink, fontFamily: 'EB Garamond, Georgia, serif' }}>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
          Novel Framework
        </h1>
        <p className="mt-1 text-[14px]" style={{ color: P.inkSoft }}>
          Build the scaffolding — logline, story bible, characters, and chapter beats — and let The Loom
          draft your first three chapters automatically. Everything is editable later.
        </p>

        <section
          className="mt-6 rounded-md border p-5"
          style={{ borderColor: `${P.gold}77`, backgroundColor: '#fbf6e6' }}
          data-testid="nf-new-form"
        >
          <div className="text-[12px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
            Start a new framework
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-[12px]" style={{ color: P.inkSoft }}>
              Working title
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="nf-new-name"
                placeholder="The Salt Garden"
                className="mt-1 w-full rounded-sm border bg-transparent p-2 text-[14px] outline-none"
                style={{ borderColor: `${P.goldDeep}66`, color: P.ink }}
              />
            </label>
            <label className="text-[12px]" style={{ color: P.inkSoft }}>
              Genre
              <input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                data-testid="nf-new-genre"
                placeholder="literary fiction"
                className="mt-1 w-full rounded-sm border bg-transparent p-2 text-[14px] outline-none"
                style={{ borderColor: `${P.goldDeep}66`, color: P.ink }}
              />
            </label>
            <label className="sm:col-span-2 text-[12px]" style={{ color: P.inkSoft }}>
              <span className="flex items-center justify-between">
                <span>Logline · one sentence</span>
                {/* iter55g10 — Draft-with-Claude starter button. Fills the
                     blank textarea with a curated pick from three AI-drafted
                     candidates. Beats the writer's-block warm-up. */}
                <button
                  type="button"
                  onClick={() => setDraftOpen(true)}
                  data-testid="nf-new-draft-loglines-btn"
                  className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider transition-colors hover:bg-primary/10"
                  style={{ borderColor: `${P.oxblood}55`, color: P.oxblood }}
                  title="Ask Claude to draft 3 candidate loglines for you"
                >
                  ✨ Draft one for me
                </button>
              </span>
              <textarea
                value={logline}
                onChange={(e) => setLogline(e.target.value.slice(0, 500))}
                data-testid="nf-new-logline"
                rows={2}
                maxLength={500}
                placeholder="A widow returns to her mother's coastal home to sell it and discovers the sea is keeping secrets."
                className="mt-1 w-full resize-none rounded-sm border bg-transparent p-2 text-[14px] outline-none"
                style={{ borderColor: `${P.goldDeep}66`, color: P.ink }}
              />
              {/* iter55g9 — live counter so writers get instant feedback
                   long before they hit the server-side cap. Capped at 500
                   (not the old 10,000) so the counter stops implying a
                   full page is expected under a field labeled "one
                   sentence" — iPad beta-tester feedback, Feb 2026. */}
              <div
                className="mt-0.5 text-right text-[10.5px]"
                data-testid="nf-new-logline-count"
                style={{ color: logline.length > 450 ? P.oxblood : P.inkSoft }}
              >
                {logline.length.toLocaleString()} / 500 characters
              </div>
            </label>
            <label className="text-[12px]" style={{ color: P.inkSoft }}>
              Chapter count · {count}
              <input
                type="range" min={5} max={60} value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                data-testid="nf-new-count"
                className="mt-2 w-full accent-current"
                style={{ accentColor: P.oxblood }}
              />
              <div className="mt-0.5 flex justify-between text-[10px]" style={{ color: P.inkSoft }}>
                <span>5</span><span>30</span><span>60</span>
              </div>
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={create}
              disabled={creating}
              data-testid="nf-new-submit"
              className="inline-flex items-center gap-2 rounded-sm px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: P.oxblood }}
              title={name.trim() ? 'Create framework' : 'Enter a working title first'}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Start framework
            </button>
          </div>
        </section>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-[12px]" style={{ color: P.inkSoft }}>
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your frameworks…
          </div>
        ) : items.length > 0 && (
          <section className="mt-8">
            <div className="text-[12px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>
              Your frameworks
            </div>
            <ul className="mt-3 space-y-2" data-testid="nf-list">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center justify-between rounded-sm border p-3"
                  style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}
                  data-testid={`nf-list-item-${it.id}`}
                >
                  <button onClick={() => onOpen(it.id)} className="text-left">
                    <div className="text-[14px] font-semibold" style={{ color: P.ink }}>
                      {it.name}
                    </div>
                    <div className="text-[11.5px]" style={{ color: P.inkSoft }}>
                      {it.genre} · {it.chapter_count} chapters
                      {it.imported_project_id && ' · imported ✓'}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onOpen(it.id)}
                      className="text-[11px] font-semibold"
                      style={{ color: P.oxblood }}
                    >
                      Open →
                    </button>
                    <button
                      onClick={() => remove(it.id)}
                      className="text-[11px]"
                      data-testid={`nf-delete-${it.id}`}
                      style={{ color: P.inkSoft }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* iter55g10 — Draft-a-logline dialog. Renders three candidate
           loglines from Claude; clicking one fills the textarea. */}
      {draftOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDraftOpen(false); }}
          data-testid="nf-draft-loglines-dialog"
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border p-5 shadow-2xl"
            style={{ backgroundColor: P.paper, borderColor: `${P.gold}88` }}
          >
            <h3 className="text-[18px] font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
              Three loglines to pick from
            </h3>
            <p className="mt-1 text-[12px]" style={{ color: P.inkSoft }}>
              Claude drafted these for a <b>{(genre || 'literary fiction').trim()}</b> novel.
              Pick one to seed your framework — edit freely after.
            </p>

            <label className="mt-4 block text-[11.5px]" style={{ color: P.inkSoft }}>
              Optional seed (a phrase, character, or setting)
              <input
                value={draftSeed}
                onChange={(e) => setDraftSeed(e.target.value)}
                placeholder="a lighthouse keeper who tests herbal remedies"
                maxLength={280}
                data-testid="nf-draft-loglines-seed"
                className="mt-1 w-full rounded-sm border bg-transparent p-2 text-[13px] outline-none"
                style={{ borderColor: `${P.goldDeep}66`, color: P.ink }}
              />
            </label>

            <div className="mt-4 space-y-2" data-testid="nf-draft-loglines-list">
              {draftLoading ? (
                <div className="grid place-items-center rounded-md border border-dashed p-8 text-[12px]"
                     style={{ borderColor: `${P.gold}77`, color: P.inkSoft }}>
                  Drafting three candidates…
                </div>
              ) : draftCandidates.length === 0 ? (
                <div className="grid place-items-center rounded-md border border-dashed p-8 text-[12px]"
                     style={{ borderColor: `${P.gold}77`, color: P.inkSoft }}>
                  No candidates yet — click <b className="mx-1">Draft</b> below.
                </div>
              ) : (
                draftCandidates.map((cand, idx) => (
                  <button
                    key={`${idx}-${String(cand).slice(0, 40)}`}
                    type="button"
                    onClick={() => pickDraftCandidate(cand)}
                    data-testid={`nf-draft-loglines-candidate-${idx}`}
                    className="group block w-full rounded-md border p-3 text-left text-[13px] leading-relaxed transition-colors hover:bg-white/60"
                    style={{ borderColor: `${P.goldDeep}55`, color: P.ink, backgroundColor: '#fdfaf0' }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] mb-1" style={{ color: P.oxblood }}>
                      Option {idx + 1}
                    </div>
                    {cand}
                    <div className="mt-1 text-[10.5px] opacity-0 transition-opacity group-hover:opacity-100" style={{ color: P.oxblood }}>
                      Click to use this logline →
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setDraftOpen(false)}
                className="rounded-sm px-3 py-1.5 text-[12px]"
                style={{ color: P.inkSoft }}
                data-testid="nf-draft-loglines-close"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => runDraft()}
                disabled={draftLoading}
                data-testid="nf-draft-loglines-redraft"
                className="inline-flex items-center gap-1 rounded-sm border px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wider hover:opacity-90 disabled:opacity-50"
                style={{ borderColor: P.oxblood, color: P.paper, backgroundColor: P.oxblood }}
              >
                {draftLoading ? 'Drafting…' : (draftCandidates.length ? 'Redraft three more' : 'Draft')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* ============================================================
 *  Section renderers
 * ============================================================ */
function Field({ label, hint, tooltip, children }) {
  return (
    <label className="block text-[12px]" style={{ color: P.inkSoft }}>
      <span className="inline-flex items-center gap-1">
        {label}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3 w-3 cursor-help opacity-50" style={{ color: P.inkSoft }} />
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px] text-[11px] leading-snug">{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
      {children}
      {hint && <div className="mt-0.5 text-[10.5px]" style={{ color: P.inkSoft }}>{hint}</div>}
    </label>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section
      className="rounded-md border p-5"
      style={{ borderColor: `${P.gold}77`, backgroundColor: '#fbf6e6' }}
    >
      <h2 className="text-[18px] font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
        {title}
      </h2>
      {subtitle && <p className="mt-0.5 text-[12.5px]" style={{ color: P.inkSoft }}>{subtitle}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function inputStyle() {
  return {
    borderColor: `${P.goldDeep}66`, color: P.ink,
    backgroundColor: 'transparent',
  };
}

// iter55f92 — Curated presets fetched once per session and cached on
// the module (survives section switches, resets on hard reload). If
// the backend is unreachable we fall back to a tiny inline list so
// the framework builder never becomes unusable.
let __presetsCache = null;
function useFrameworkPresets() {
  const [presets, setPresets] = useState(__presetsCache);
  useEffect(() => {
    if (__presetsCache) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/framework/presets`);
        if (cancelled) return;
        __presetsCache = r.data;
        setPresets(r.data);
      } catch {
        if (cancelled) return;
        __presetsCache = {
          character_traits: ['Brave', 'Loyal', 'Cunning', 'Empathetic', 'Impulsive', 'Stoic'],
          tones: ['Elegiac', 'Wry', 'Tender', 'Cinematic'],
          voices: ['literary', 'commercial', 'lyrical'],
          location_kinds: ['Place', 'City', 'Building', 'Wilderness'],
        };
        setPresets(__presetsCache);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return presets;
}

function ChipPicker({ options, selected, onToggle, onAddCustom, testIdPrefix, maxChips = 12 }) {
  const [custom, setCustom] = useState('');
  const commitCustom = () => {
    const v = (custom || '').trim();
    if (!v || selected.includes(v) || selected.length >= maxChips) return;
    onAddCustom(v);
    setCustom('');
  };
  return (
    <div className="space-y-1.5" data-testid={testIdPrefix}>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              disabled={!on && selected.length >= maxChips}
              data-testid={`${testIdPrefix}-preset-${opt.toLowerCase().replace(/\s+/g, '-')}`}
              className="rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition disabled:opacity-40"
              style={{
                borderColor: on ? P.oxblood : `${P.gold}88`,
                backgroundColor: on ? `${P.oxblood}18` : 'transparent',
                color: on ? P.oxblood : P.inkSoft,
              }}
            >
              {on ? '✓ ' : '+ '}{opt}
            </button>
          );
        })}
      </div>
      {selected.filter((s) => !options.includes(s)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.filter((s) => !options.includes(s)).map((s) => (
            <span
              key={s}
              data-testid={`${testIdPrefix}-custom-${s.toLowerCase().replace(/\s+/g, '-')}`}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: `${P.oxblood}18`, color: P.oxblood }}
            >
              ✓ {s}
              <button type="button" onClick={() => onToggle(s)} className="text-[10px] opacity-70 hover:opacity-100">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustom(); } }}
          placeholder={`Add custom (${selected.length}/${maxChips})`}
          data-testid={`${testIdPrefix}-custom-input`}
          className="w-40 rounded-sm border bg-transparent px-2 py-0.5 text-[11px] outline-none"
          style={inputStyle()}
          disabled={selected.length >= maxChips}
        />
        <button
          type="button"
          onClick={commitCustom}
          data-testid={`${testIdPrefix}-custom-add`}
          className="rounded-sm px-1.5 py-0.5 text-[11px] font-semibold"
          style={{ color: P.oxblood }}
        >
          + Add
        </button>
      </div>
    </div>
  );
}

function computeActDistribution(structure, chapCount) {
  const total = Math.max(1, chapCount || 1);
  const counts = {};
  for (let n = 1; n <= total; n++) {
    const p = n / total;
    let label;
    if (structure === '5-act') {
      if (p <= 0.15)      label = 'Exposition';
      else if (p <= 0.45) label = 'Rising';
      else if (p <= 0.57) label = 'Climax';
      else if (p <= 0.85) label = 'Falling';
      else                label = 'Resolution';
    } else if (structure === '7-act') {
      if (p <= 0.12)      label = 'Hook';
      else if (p <= 0.30) label = 'Plot Turn 1';
      else if (p <= 0.45) label = 'Pinch 1';
      else if (p <= 0.58) label = 'Midpoint';
      else if (p <= 0.70) label = 'Pinch 2';
      else if (p <= 0.88) label = 'Plot Turn 2';
      else                label = 'Resolution';
    } else {
      if (p <= 0.25)      label = 'Act I';
      else if (p <= 0.75) label = 'Act II';
      else                label = 'Act III';
    }
    counts[label] = (counts[label] || 0) + 1;
  }
  const order =
    structure === '5-act' ? ['Exposition', 'Rising', 'Climax', 'Falling', 'Resolution']
    : structure === '7-act' ? ['Hook', 'Plot Turn 1', 'Pinch 1', 'Midpoint', 'Pinch 2', 'Plot Turn 2', 'Resolution']
    : ['Act I', 'Act II', 'Act III'];
  return order.filter((l) => counts[l]).map((l) => ({ label: l, count: counts[l] }));
}

function SectionBasics({ f, update }) {
  return (
    <SectionCard
      title="Basics"
      subtitle="Title, genre, and the one-sentence pitch. Everything downstream anchors to these."
    >
      <Field label="Working title">
        <input
          value={f.name || ''}
          onChange={(e) => update((cur) => ({ ...cur, name: e.target.value }))}
          data-testid="nf-basics-name"
          className="mt-1 w-full rounded-sm border p-2 text-[14px] outline-none"
          style={inputStyle()}
        />
      </Field>
      <Field label="Genre">
        <input
          value={f.genre || ''}
          onChange={(e) => update((cur) => ({ ...cur, genre: e.target.value }))}
          data-testid="nf-basics-genre"
          className="mt-1 w-full rounded-sm border p-2 text-[14px] outline-none"
          style={inputStyle()}
        />
      </Field>
      <Field label="Logline · one sentence, active voice, protagonist + obstacle + stakes">
        <textarea
          value={f.logline || ''}
          onChange={(e) => update((cur) => ({ ...cur, logline: e.target.value.slice(0, 500) }))}
          data-testid="nf-basics-logline"
          data-bible-field="framework.logline"
          rows={3}
          maxLength={500}
          className="mt-1 w-full resize-none rounded-sm border p-2 text-[14px] outline-none"
          style={inputStyle()}
        />
        {/* iter55g9 — live counter matching backend cap. Lowered from
             10,000 to 500 so a field explicitly labeled "one sentence"
             stops implying a page of text is normal here. */}
        <div
          className="mt-0.5 text-right text-[10.5px]"
          data-testid="nf-basics-logline-count"
          style={{ color: (f.logline || '').length > 450 ? P.oxblood : P.inkSoft }}
        >
          {((f.logline || '').length).toLocaleString()} / 500 characters
        </div>
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={`Chapter count · ${f.chapter_count}`}
          tooltip="Number of chapters Claude will draft. A typical novel is 20–40 chapters. Affects words-per-chapter calculation."
        >
          <input
            type="range" min={5} max={60} value={f.chapter_count}
            onChange={(e) => update((cur) => ({ ...cur, chapter_count: Number(e.target.value) }))}
            data-testid="nf-basics-count"
            className="mt-2 w-full"
            style={{ accentColor: P.oxblood }}
          />
          <div className="mt-0.5 flex items-center justify-between text-[10px]" style={{ color: P.inkSoft }}>
            <span>5</span><span>30</span><span>60</span>
            <span className="ml-auto font-medium" style={{ color: P.oxblood }}>
              ~{Math.round((f.target_words || 80000) / Math.max(1, f.chapter_count || 1)).toLocaleString()} words/ch
            </span>
          </div>
        </Field>
        <Field
          label={`Target word count · ${(f.target_words || 0).toLocaleString()}`}
          tooltip="Total manuscript length. Divided by chapter count to set Claude's per-chapter target."
        >
          <input
            type="range" min={10000} max={250000} step={1000} value={f.target_words}
            onChange={(e) => update((cur) => ({ ...cur, target_words: Number(e.target.value) }))}
            data-testid="nf-basics-words"
            className="mt-2 w-full"
            style={{ accentColor: P.oxblood }}
          />
          <div className="mt-0.5 flex justify-between text-[10px]" style={{ color: P.inkSoft }}>
            <span>10k</span><span>80k</span><span>250k</span>
          </div>
        </Field>
      </div>
      {/* iter-romance25 — Act-structure toggle. 3-act, 5-act, and 7-act (Dan Wells)
          are supported. Backend `_act_for_chapter` routes to the right label ladder. */}
      <Field label="Act structure" tooltip="Controls how chapters are labelled and what beats Claude aims for. Changing structure re-labels all chapter act badges on next save.">
        <div className="mt-1 inline-flex overflow-hidden rounded-md border" style={{ borderColor: P.oxblood }} data-testid="nf-basics-structure" data-bible-field="framework.structure"
             ref={(el) => { if (el && !el.__toured) { el.__toured = true; runTour('framework-structure-open'); } }}>
          {[
            { value: '3-act', label: '3-Act (Classical)' },
            { value: '5-act', label: '5-Act (Freytag)' },
            { value: '7-act', label: '7-Act (Dan Wells)' },
          ].map((opt) => {
            const active = (f.structure || '3-act') === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update((cur) => ({ ...cur, structure: opt.value }))}
                data-testid={`nf-basics-structure-${opt.value}`}
                className="px-3 py-1.5 text-[12px] font-semibold transition"
                style={{
                  backgroundColor: active ? P.oxblood : 'transparent',
                  color:           active ? P.paper   : P.ink,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="mt-1 text-[10.5px]" style={{ color: P.inkSoft }}>
          {(f.structure || '3-act') === '5-act'
            ? 'Chapters split into Exposition · Rising · Climax · Falling · Denouement.'
            : (f.structure || '3-act') === '7-act'
            ? 'Seven turning points: Hook → Plot Turn 1 → Pinch 1 → Midpoint → Pinch 2 → Plot Turn 2 → Resolution.'
            : 'Chapters split into Act I · Act II · Act III.'}
        </div>
        {/* Act distribution preview — live chip row */}
        <div className="mt-2 flex flex-wrap gap-1" data-testid="nf-act-distribution">
          {computeActDistribution(f.structure, f.chapter_count).map(({ label, count }) => (
            <span
              key={label}
              className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
              style={{ borderColor: `${P.oxblood}55`, color: P.oxblood, backgroundColor: `${P.oxblood}0d` }}
            >
              {label} · {count} ch
            </span>
          ))}
        </div>
      </Field>
      {/* Prompt-caching toggle */}
      <Field
        label="Prompt caching"
        tooltip="Caches system prompt + manuscript context between chapters for ~70% cost savings. Cache expires after 1 hour of inactivity — keep drafting within 60 min gaps."
      >
        <div className="mt-1 flex items-center gap-3">
          <button
            type="button"
            onClick={() => update((cur) => ({ ...cur, prompt_caching: !(cur.prompt_caching ?? true) }))}
            data-testid="nf-basics-caching"
            className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none"
            style={{ backgroundColor: (f.prompt_caching ?? true) ? P.oxblood : P.inkSoft }}
            aria-pressed={(f.prompt_caching ?? true)}
          >
            <span
              className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5"
              style={{ transform: (f.prompt_caching ?? true) ? 'translateX(1.125rem)' : 'translateX(0.125rem)' }}
            />
          </button>
          <span className="text-[11px]" style={{ color: P.inkSoft }}>
            {(f.prompt_caching ?? true)
              ? 'On — context cached for 1 h between chapters (recommended)'
              : 'Off — fresh context each call (higher cost)'}
          </span>
        </div>
      </Field>
    </SectionCard>
  );
}

function SectionBible({ f, update }) {
  const b = f.bible || {};
  const t = f.toggles || {};
  const setBible = (k, v) => update((cur) => ({ ...cur, bible: { ...(cur.bible || {}), [k]: v } }));
  const setToggle = (k, v) => update((cur) => ({ ...cur, toggles: { ...(cur.toggles || {}), [k]: v } }));

  return (
    <>
      <SectionCard
        title="Voice · POV · Tense"
        subtitle="How the story speaks. Sets the model's baseline before anything else."
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Point of view">
            <select
              value={b.pov || 'third-limited'}
              onChange={(e) => setBible('pov', e.target.value)}
              data-testid="nf-bible-pov"
              className="mt-1 w-full rounded-sm border p-2 text-[13px] outline-none"
              style={inputStyle()}
            >
              {POV_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Tense">
            <select
              value={b.tense || 'past'}
              onChange={(e) => setBible('tense', e.target.value)}
              data-testid="nf-bible-tense"
              className="mt-1 w-full rounded-sm border p-2 text-[13px] outline-none"
              style={inputStyle()}
            >
              {TENSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Voice">
            <select
              value={b.voice || 'literary'}
              onChange={(e) => setBible('voice', e.target.value)}
              data-testid="nf-bible-voice"
              className="mt-1 w-full rounded-sm border p-2 text-[13px] outline-none"
              style={inputStyle()}
            >
              {VOICE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        </div>
      </SectionCard>

      <SectionCard
        title="World · Themes · Tone"
        subtitle="A few sentences each. Claude reads all three verbatim when drafting."
      >
        <Field label="World / setting">
          <textarea
            value={b.world || ''}
            onChange={(e) => setBible('world', e.target.value)}
            data-testid="nf-bible-world"
            rows={3}
            placeholder="A late-19th-century Cornish fishing village; the old lighthouse is unmanned but not empty."
            className="mt-1 w-full resize-none rounded-sm border p-2 text-[13px] outline-none"
            style={inputStyle()}
          />
        </Field>
        <Field label="Themes">
          <textarea
            value={b.themes || ''}
            onChange={(e) => setBible('themes', e.target.value)}
            data-testid="nf-bible-themes"
            data-bible-field="story.themes"
            rows={2}
            placeholder="grief; inheritance; what we owe the dead."
            className="mt-1 w-full resize-none rounded-sm border p-2 text-[13px] outline-none"
            style={inputStyle()}
          />
        </Field>
        <Field label="Tone">
          <input
            value={b.tone || ''}
            onChange={(e) => setBible('tone', e.target.value)}
            data-testid="nf-bible-tone"
            placeholder="melancholic, lyrical, tightening into dread"
            className="mt-1 w-full rounded-sm border p-2 text-[13px] outline-none"
            style={inputStyle()}
          />
        </Field>
      </SectionCard>

      <SectionCard
        title="Story toggles"
        subtitle="Check the ones that apply. Off by default; changing later regenerates the AI prompt."
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            ['include_prologue',    'Include a prologue'],
            ['include_epilogue',    'Include an epilogue'],
            ['include_the_end',     'Mark the last chapter as "The End"'],
            ['dark_themes',         'Dark themes (violence / trauma)'],
            ['romance_thread',      'Romantic subplot'],
            ['dual_timeline',       'Dual timeline'],
            ['unreliable_narrator', 'Unreliable narrator'],
          ].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-[13px]" style={{ color: P.ink }}>
              <input
                type="checkbox"
                checked={!!t[k]}
                onChange={(e) => setToggle(k, e.target.checked)}
                data-testid={`nf-toggle-${k}`}
                style={{ accentColor: P.oxblood }}
              />
              {lbl}
            </label>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Import an existing bible"
        subtitle="Paste notes, worldbuilding fragments, or a prior outline. Claude will read this alongside the structured fields when drafting."
      >
        <textarea
          value={b.imported_notes || ''}
          onChange={(e) => setBible('imported_notes', e.target.value)}
          data-testid="nf-bible-imported"
          rows={8}
          placeholder="Paste anything — character sheets, worldbuilding, timeline notes…"
          className="mt-1 w-full resize-y rounded-sm border p-2 text-[12.5px] outline-none"
          style={{ ...inputStyle(), minHeight: '160px', fontFamily: 'Georgia, serif' }}
        />
        <div className="mt-1 text-[10.5px]" style={{ color: P.inkSoft }}>
          {(b.imported_notes || '').length.toLocaleString()} / 20,000 characters
        </div>
      </SectionCard>
    </>
  );
}

function SectionCharacters({ f, update }) {
  const chars = f.characters || [];
  const presets = useFrameworkPresets();
  const traitOptions = presets?.character_traits || [];
  const addCharacter = (role = 'Supporting') => {
    update((cur) => ({
      ...cur,
      characters: [
        ...(cur.characters || []),
        { id: `chr_${Math.random().toString(36).slice(2, 12)}`,
          name: '', role, want: '', need: '', flaw: '', arc: '', description: '', traits: [] },
      ],
    }));
  };
  const patchChar = (id, patch) => {
    update((cur) => ({
      ...cur,
      characters: (cur.characters || []).map((c) => c.id === id ? { ...c, ...patch } : c),
    }));
  };
  const removeChar = (id) => {
    update((cur) => ({ ...cur, characters: (cur.characters || []).filter((c) => c.id !== id) }));
  };
  const toggleTrait = (id, trait) => {
    update((cur) => ({
      ...cur,
      characters: (cur.characters || []).map((c) => {
        if (c.id !== id) return c;
        const cur_traits = c.traits || [];
        const next = cur_traits.includes(trait)
          ? cur_traits.filter((t) => t !== trait)
          : [...cur_traits, trait];
        return { ...c, traits: next };
      }),
    }));
  };
  const addCustomTrait = (id, trait) => {
    update((cur) => ({
      ...cur,
      characters: (cur.characters || []).map((c) =>
        c.id !== id ? c : { ...c, traits: [...(c.traits || []), trait] }),
    }));
  };

  return (
    <SectionCard
      title="Characters"
      subtitle="At minimum: one protagonist, one antagonist, one foil. Each character needs a want, a need, and a flaw for Claude to write dimensional scenes."
    >
      <div className="flex flex-wrap gap-2" data-testid="nf-char-role-picker">
        {ROLE_OPTIONS.map((r) => (
          <button
            key={r}
            onClick={() => addCharacter(r)}
            data-testid={`nf-char-add-${r.toLowerCase().replace(/\s+/g, '-')}`}
            className="rounded-full border px-3 py-1 text-[11.5px] font-semibold transition"
            style={{
              borderColor: `${P.oxblood}88`,
              backgroundColor: P.paperDeep,
              color: P.oxblood,
            }}
          >
            + {r}
          </button>
        ))}
      </div>

      <ul className="space-y-3" data-testid="nf-char-list">
        {chars.length === 0 && (
          <li className="rounded-sm border border-dashed p-4 text-center text-[12px]"
              style={{ borderColor: `${P.gold}88`, color: P.inkSoft }}>
            No characters yet — click a role above to add one.
          </li>
        )}
        {chars.map((c) => (
          <li
            key={c.id}
            data-testid={`nf-char-${c.id}`}
            className="rounded-md border p-3"
            style={{ borderColor: `${P.oxblood}55`, backgroundColor: P.paperDeep }}
          >
            <div className="flex items-start gap-2">
              <input
                value={c.name}
                onChange={(e) => patchChar(c.id, { name: e.target.value })}
                data-testid={`nf-char-name-${c.id}`}
                placeholder="Name"
                className="flex-1 rounded-sm border bg-transparent px-2 py-1 text-[13.5px] font-semibold outline-none"
                style={inputStyle()}
              />
              <select
                value={c.role}
                onChange={(e) => patchChar(c.id, { role: e.target.value })}
                data-testid={`nf-char-role-${c.id}`}
                className="rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              >
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button
                onClick={() => removeChar(c.id)}
                data-testid={`nf-char-remove-${c.id}`}
                className="rounded-sm p-1"
                style={{ color: P.oxblood }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {[['want','Wants…'], ['need','Needs…'], ['flaw','Flaw…']].map(([k, ph]) => (
                <input
                  key={k}
                  value={c[k] || ''}
                  onChange={(e) => patchChar(c.id, { [k]: e.target.value })}
                  data-testid={`nf-char-${k}-${c.id}`}
                  placeholder={ph}
                  className="w-full rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                  style={inputStyle()}
                />
              ))}
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <textarea
                value={c.arc || ''}
                onChange={(e) => patchChar(c.id, { arc: e.target.value })}
                data-testid={`nf-char-arc-${c.id}`}
                rows={2}
                placeholder="Arc — how they change from beginning to end"
                className="w-full resize-none rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              />
              <textarea
                value={c.description || ''}
                onChange={(e) => patchChar(c.id, { description: e.target.value })}
                data-testid={`nf-char-desc-${c.id}`}
                rows={2}
                placeholder="Physical & voice notes"
                className="w-full resize-none rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              />
            </div>
            <div className="mt-2">
              <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.2em]"
                   style={{ color: P.oxblood }}>
                Traits
              </div>
              <ChipPicker
                options={traitOptions}
                selected={c.traits || []}
                onToggle={(t) => toggleTrait(c.id, t)}
                onAddCustom={(t) => addCustomTrait(c.id, t)}
                testIdPrefix={`nf-char-traits-${c.id}`}
                maxChips={12}
              />
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function SectionLocations({ f, update }) {
  const locations = f.locations || [];
  const presets = useFrameworkPresets();
  const kindOptions = presets?.location_kinds || ['Place', 'City', 'Building'];
  const addLocation = () => {
    update((cur) => ({
      ...cur,
      locations: [
        ...(cur.locations || []),
        { id: `loc_${Math.random().toString(36).slice(2, 12)}`,
          name: '', kind: 'Place', description: '', mood: '', era: '' },
      ],
    }));
  };
  const patchLoc = (id, patch) => {
    update((cur) => ({
      ...cur,
      locations: (cur.locations || []).map((l) => l.id === id ? { ...l, ...patch } : l),
    }));
  };
  const removeLoc = (id) => {
    update((cur) => ({ ...cur, locations: (cur.locations || []).filter((l) => l.id !== id) }));
  };
  return (
    <SectionCard
      title="Locations"
      subtitle="Reusable places, cities, buildings — anywhere your scenes happen. Chapters can reference these by name so Claude sees the same setting each time."
    >
      <button
        onClick={addLocation}
        data-testid="nf-loc-add"
        className="rounded-full border px-3 py-1 text-[11.5px] font-semibold transition"
        style={{ borderColor: `${P.oxblood}88`, backgroundColor: P.paperDeep, color: P.oxblood }}
      >
        + Add location
      </button>
      <ul className="space-y-3" data-testid="nf-loc-list">
        {locations.length === 0 && (
          <li className="rounded-sm border border-dashed p-4 text-center text-[12px]"
              style={{ borderColor: `${P.gold}88`, color: P.inkSoft }}>
            No locations yet — add one to reuse it across chapters.
          </li>
        )}
        {locations.map((l) => (
          <li
            key={l.id}
            data-testid={`nf-loc-${l.id}`}
            className="rounded-md border p-3"
            style={{ borderColor: `${P.oxblood}55`, backgroundColor: P.paperDeep }}
          >
            <div className="flex items-start gap-2">
              <input
                value={l.name}
                onChange={(e) => patchLoc(l.id, { name: e.target.value })}
                data-testid={`nf-loc-name-${l.id}`}
                placeholder="Location name"
                className="flex-1 rounded-sm border bg-transparent px-2 py-1 text-[13.5px] font-semibold outline-none"
                style={inputStyle()}
              />
              <select
                value={l.kind || 'Place'}
                onChange={(e) => patchLoc(l.id, { kind: e.target.value })}
                data-testid={`nf-loc-kind-${l.id}`}
                className="rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              >
                {kindOptions.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <button
                onClick={() => removeLoc(l.id)}
                data-testid={`nf-loc-remove-${l.id}`}
                className="rounded-sm p-1"
                style={{ color: P.oxblood }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <input
                value={l.mood || ''}
                onChange={(e) => patchLoc(l.id, { mood: e.target.value })}
                data-testid={`nf-loc-mood-${l.id}`}
                placeholder="Mood — e.g. incense-heavy, gothic hush"
                className="w-full rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              />
              <input
                value={l.era || ''}
                onChange={(e) => patchLoc(l.id, { era: e.target.value })}
                data-testid={`nf-loc-era-${l.id}`}
                placeholder="Era — e.g. 1920s, near-future, medieval"
                className="w-full rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
                style={inputStyle()}
              />
            </div>
            <textarea
              value={l.description || ''}
              onChange={(e) => patchLoc(l.id, { description: e.target.value })}
              data-testid={`nf-loc-desc-${l.id}`}
              rows={2}
              placeholder="Sensory details: what one sees, hears, smells here"
              className="mt-2 w-full resize-none rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
              style={inputStyle()}
            />
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// Group-A — Pre-flight TTL warning dialog shown before every draftFromBeat call.
// Displays a live gap timer, 3 caching safety rules, and a per-run caching toggle.
function DraftPreflightDialog({ open, onClose, onProceed, cachingDefault, lastDraftAt, chapterNum }) {
  const [caching, setCaching] = useState(cachingDefault ?? true);

  // Sync caching toggle to framework-level default whenever dialog opens.
  useEffect(() => {
    if (open) setCaching(cachingDefault ?? true);
  }, [open, cachingDefault]);

  // Live gap timer — ticks every second while dialog is open.
  const [gapSecs, setGapSecs] = useState(0);
  useEffect(() => {
    if (!open || !lastDraftAt) { setGapSecs(0); return; }
    const tick = () => setGapSecs(Math.floor((Date.now() - lastDraftAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, lastDraftAt]);

  if (!open) return null;

  const gapMins    = Math.floor(gapSecs / 60);
  const gapSecsRem = gapSecs % 60;
  const gapNear    = gapMins >= 55;
  const gapExpired = gapMins >= 60;
  const gapStr     = lastDraftAt
    ? `${gapMins}:${String(gapSecsRem).padStart(2, '0')} since last chapter`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="draft-preflight-dialog"
    >
      <div
        className="w-full max-w-sm rounded-lg p-5 shadow-xl"
        style={{ backgroundColor: P.paper, border: `1.5px solid ${P.gold}88` }}
      >
        <div className="text-[15px] font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}>
          Draft Chapter {chapterNum}
        </div>

        {/* Gap timer — only shown after first chapter in session */}
        {gapStr && (
          <div
            className="mt-3 flex items-center gap-2 rounded-sm px-2.5 py-2 text-[11.5px] font-medium"
            style={{
              backgroundColor: gapExpired ? `${P.oxblood}22` : gapNear ? '#b8720022' : `${P.gold}22`,
              borderLeft: `3px solid ${gapExpired ? P.oxblood : gapNear ? '#b87200' : P.gold}`,
              color: gapExpired ? P.oxblood : gapNear ? '#b87200' : P.ink,
            }}
            data-testid="preflight-gap-timer"
          >
            <span className="font-mono font-semibold text-[13px]">{gapStr}</span>
            {gapExpired && <span className="ml-1">— cache expired, this call will miss</span>}
            {!gapExpired && gapNear && <span className="ml-1">— cache expiring soon</span>}
          </div>
        )}

        {/* Safety rules */}
        <ul className="mt-3 space-y-2 text-[11.5px]" style={{ color: P.inkSoft }}>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-[10px]" style={{ color: P.oxblood }}>▸</span>
            Each chapter must start within <strong>60 minutes</strong> of the previous one — gaps reset the cache and cost more.
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-[10px]" style={{ color: P.oxblood }}>▸</span>
            If a request stalls, cancel and retry manually — <strong>never let the client retry silently</strong> past 60 min.
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-[10px]" style={{ color: P.oxblood }}>▸</span>
            Run <strong>at most 2 novels concurrently</strong> — parallel jobs beyond that may stall mid-draft.
          </li>
        </ul>

        {/* Per-run caching toggle */}
        <div
          className="mt-4 flex items-center gap-3 rounded-sm border p-2.5"
          style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}
        >
          <button
            type="button"
            onClick={() => setCaching((v) => !v)}
            data-testid="preflight-caching-toggle"
            className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none"
            style={{ backgroundColor: caching ? P.oxblood : P.inkSoft }}
            aria-pressed={caching}
          >
            <span
              className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5"
              style={{ transform: caching ? 'translateX(1.125rem)' : 'translateX(0.125rem)' }}
            />
          </button>
          <div>
            <div className="text-[11.5px] font-semibold" style={{ color: P.ink }}>Prompt caching</div>
            <div className="text-[10.5px]" style={{ color: P.inkSoft }}>
              {caching ? 'On — 1-hour context cache · saves ~70% on repeat calls' : 'Off — fresh context · higher cost'}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="preflight-cancel"
            className="rounded-sm border px-3 py-1.5 text-[12px] font-semibold"
            style={{ borderColor: `${P.oxblood}66`, color: P.oxblood }}
          >
            Cancel
          </button>
          <button
            onClick={() => onProceed(caching)}
            data-testid="preflight-proceed"
            className="rounded-sm px-3 py-1.5 text-[12px] font-semibold text-white"
            style={{ backgroundColor: P.oxblood }}
          >
            Draft chapter {chapterNum}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionChapters({ f, update }) {
  const chapters  = f.chapters || [];
  const chars     = f.characters || [];
  const locations = f.locations || [];

  // iter55g19 — Inferred beats from the manuscript analyzer live at
  // `f.analysis.remaining_chapters_outline`. We also surface transition
  // hints and per-chapter arc peaks so the writer sees everything Claude
  // extracted in one place, per chapter.
  const beatByNumber = useMemo(() => {
    const m = new Map();
    const inferredBeats = f.analysis?.remaining_chapters_outline || [];
    inferredBeats.forEach((b) => {
      const n = Number(b.number);
      if (!Number.isNaN(n) && b.beat) m.set(n, String(b.beat));
    });
    return m;
  }, [f.analysis?.remaining_chapters_outline]);

  const transitionByFrom = useMemo(() => {
    const m = new Map();
    const transitions = f.analysis?.chapter_transitions || [];
    transitions.forEach((t) => {
      const from = Number(t.from);
      if (!Number.isNaN(from) && t.note) m.set(from, String(t.note));
    });
    return m;
  }, [f.analysis?.chapter_transitions]);

  const arcsByChapter = useMemo(() => {
    const m = new Map();
    const arcs = f.analysis?.characters || [];
    arcs.forEach((ch) => {
      (ch.peaks_in_chapters || []).forEach((n) => {
        const num = Number(n);
        if (Number.isNaN(num)) return;
        const list = m.get(num) || [];
        list.push({ name: ch.name, arc: ch.arc_summary });
        m.set(num, list);
      });
    });
    return m;
  }, [f.analysis?.characters]);

  const [refreshing, setRefreshing] = useState(false);
  const canRefresh = Boolean(f.imported_project_id);
  // Group-A — track when the last chapter draft completed so ChapterRow
  // can show a live TTL gap timer in the pre-flight dialog.
  const [lastDraftAt, setLastDraftAt] = useState(null);
  const handleDraftComplete = useCallback(() => setLastDraftAt(Date.now()), []);

  const refreshFromManuscript = async () => {
    if (!canRefresh) {
      toast.info('Link this framework to a project first (Import project button).');
      return;
    }
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/framework/${f.id}/generate-from-manuscript`);
      update(() => data);   // wholesale replace — server returns the full doc
      const n = (data?.analysis?.remaining_chapters_outline || []).length;
      toast.success(`Refreshed — ${n} inferred beat${n === 1 ? '' : 's'} pulled from your manuscript.`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Refresh failed.'));
    } finally {
      setRefreshing(false);
    }
  };

  const patchChapter = (n, patch) => {
    update((cur) => ({
      ...cur,
      chapters: (cur.chapters || []).map((c) => c.number === n ? { ...c, ...patch } : c),
    }));
  };

  // Apply an inferred beat to a chapter's `transition` field. If the
  // writer already wrote a transition, append rather than overwrite.
  const applyBeat = (chapter, beat) => {
    const cur = String(chapter.transition || '').trim();
    const next = cur
      ? `${cur}\n\nInferred beat: ${beat}`
      : String(beat);
    patchChapter(chapter.number, { transition: next });
    toast.success(`Applied inferred beat to Ch ${chapter.number}.`);
  };

  const dismissBeat = (n) => {
    // Remove the beat locally + persist by dropping it from the analysis
    // array. Non-destructive to any *edits* the writer already made.
    update((cur) => {
      const analysis = cur.analysis || {};
      const outline = (analysis.remaining_chapters_outline || [])
        .filter((b) => Number(b.number) !== Number(n));
      return { ...cur, analysis: { ...analysis, remaining_chapters_outline: outline } };
    });
  };

  return (
    <SectionCard
      title={`Chapters (${chapters.length})`}
      subtitle="Each transition must be 150–500 words. Claude uses it as the scene brief when drafting."
    >
      {/* iter55g19 — Manuscript-driven beat toolbar. */}
      <div
        className="mb-3 flex flex-wrap items-center gap-2 rounded-sm border p-2 text-[11.5px]"
        style={{ borderColor: `${P.gold}77`, backgroundColor: `${P.gold}10`, color: P.ink }}
        data-testid="nf-beats-toolbar"
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: P.oxblood }} />
        {beatByNumber.size > 0 ? (
          <span>
            <b>{beatByNumber.size}</b> inferred beat{beatByNumber.size === 1 ? '' : 's'} available from your manuscript — expand any chapter to review and Apply.
          </span>
        ) : canRefresh ? (
          <span>Pull chapter beats, transitions and character arcs from what you've already written.</span>
        ) : (
          <span>Link this framework to a project (Import project button) to enable manuscript-driven beats.</span>
        )}
        <button
          onClick={refreshFromManuscript}
          disabled={refreshing || !canRefresh}
          data-testid="nf-beats-refresh"
          className="ml-auto inline-flex items-center gap-1 rounded-sm px-2 py-0.5 font-semibold text-white transition disabled:opacity-40"
          style={{ backgroundColor: P.oxblood }}
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          {refreshing ? 'Analyzing…' : (beatByNumber.size ? 'Refresh from manuscript' : 'Analyze manuscript')}
        </button>
      </div>

      <ul className="space-y-3">
        {chapters.map((c) => (
          <ChapterRow
            key={c.number}
            chapter={c}
            characters={chars}
            locations={locations}
            frameworkId={f.id}
            projectId={f.imported_project_id || null}
            targetWords={f.target_words || 0}
            inferredBeat={beatByNumber.get(c.number) || null}
            handOffNote={transitionByFrom.get(c.number) || null}
            peakingArcs={arcsByChapter.get(c.number) || []}
            onPatch={(patch) => patchChapter(c.number, patch)}
            onApplyBeat={(beat) => applyBeat(c, beat)}
            onDismissBeat={() => dismissBeat(c.number)}
            promptCachingDefault={f.prompt_caching ?? true}
            lastDraftAt={lastDraftAt}
            onDraftComplete={handleDraftComplete}
          />
        ))}
      </ul>
    </SectionCard>
  );
}

function ChapterRow({ chapter: c, characters, locations = [], frameworkId,
                      projectId, targetWords,
                      inferredBeat, handOffNote, peakingArcs = [],
                      onPatch, onApplyBeat, onDismissBeat,
                      promptCachingDefault, lastDraftAt, onDraftComplete }) {
  const [suggestion, setSuggestion] = useState('');
  const [loadingSug, setLoadingSug] = useState(false);
  // iter55g20 — Live stream state for the ⚡ Draft-from-beat button.
  const [streaming, setStreaming]     = useState(false);
  const [streamText, setStreamText]   = useState('');
  const [streamError, setStreamError] = useState('');
  // Group-A — pre-flight dialog + cache status indicator.
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [cacheStatus,   setCacheStatus]   = useState(null); // {read,write,savedUsd}|null
  const wc = c.transition_words ?? countWords(c.transition || '');
  const wcStatus =
    wc === 0 ? { color: P.inkSoft, label: 'not started' } :
    wc < 150 ? { color: P.oxblood, label: `${wc} words — needs ${150 - wc} more` } :
    wc > 500 ? { color: P.oxblood, label: `${wc} words — trim ${wc - 500}` } :
    { color: P.verdant, label: `${wc} words · ready` };

  const askSuggest = async () => {
    setLoadingSug(true); setSuggestion('');
    try {
      const r = await axios.post(`${API}/framework/${frameworkId}/chapters/${c.number}/suggest`, {});
      setSuggestion(r.data.suggestion || '');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Suggestion failed.'));
    } finally { setLoadingSug(false); }
  };

  // iter55g20 — Draft this specific chapter from the inferred beat.
  // Applies the beat to `transition`, then streams /generate/chapter
  // and shows a live word count. Requires the framework to be linked
  // to a project (chapters need to exist as persisted docs).
  // Group-A: accepts `caching` flag from the pre-flight dialog.
  const draftFromBeat = async (caching = true) => {
    if (!projectId) {
      toast.error('Link this framework to a project to draft from a beat.');
      return;
    }
    if (!inferredBeat) return;

    // 1. Apply the beat locally so Claude uses it as guidance. This
    //    also triggers the debounced auto-save.
    onApplyBeat?.(inferredBeat);

    // 2. Look up this chapter's persisted document id by number.
    let chapterId;
    try {
      const rows = await chaptersApi.list(projectId);
      const match = rows.find((r) => Number(r.number) === Number(c.number));
      if (!match) throw new Error(`Chapter ${c.number} not found in linked project.`);
      chapterId = match.id;
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not find the chapter.'));
      return;
    }

    setStreaming(true);
    setStreamText('');
    setStreamError('');
    setCacheStatus(null);
    let buf = '';
    let sawDone = false;
    try {
      await streamSSE('/stream/generate/chapter',
        { project_id: projectId, chapter_id: chapterId,
          user_guidance: `Draft this chapter from the inferred beat: ${inferredBeat}`,
          prompt_caching: caching },
        {
          onEvent: (ev) => {
            if (ev.event === 'meta') {
              toast.info(`Drafting Chapter ${ev.data.number}… (target ~${(ev.data.target_words || 0).toLocaleString()} words)`);
            } else if (ev.event === 'delta') {
              buf += (ev.data?.text || '');
              setStreamText(buf);
            } else if (ev.event === 'done') {
              sawDone = true;
              // Group-A — capture cache diagnostics from the done frame.
              const cacheRead  = ev.data?.cache_read  ?? 0;
              const cacheWrite = ev.data?.cache_write ?? 0;
              const savedUsd   = ev.data?.saved_usd   ?? 0;
              setCacheStatus({ read: cacheRead, write: cacheWrite, savedUsd });
              onDraftComplete?.();
              toast.success(`Chapter ${c.number} drafted (${ev.data?.chapter?.words || 0} words).`);
            } else if (ev.event === 'error') {
              setStreamError(ev.data?.message || 'Generation failed.');
              toast.error(`Generation error: ${ev.data?.message || 'unknown'}`);
            }
          },
          onError: (err) => {
            setStreamError(String(err));
            toast.error('Stream connection failed.');
          },
        });
      // Safety net: if the stream closed cleanly but no `done` frame
      // arrived (older backends, or frame-parser drift), fall back to
      // fetching the persisted chapter so the writer isn't left in the
      // dark. The server always persists whether or not `done` shipped.
      if (!sawDone && !streamError) {
        try {
          const fresh = await chaptersApi.get(chapterId);
          if (fresh?.content) {
            setStreamText(fresh.content);
            onDraftComplete?.();
            toast.success(`Chapter ${c.number} drafted (${fresh.words || 0} words).`);
          } else {
            setStreamError('Stream ended without a done frame — nothing was saved.');
          }
        } catch {
          setStreamError('Stream ended without a done frame — could not fetch the chapter.');
        }
      }
    } finally {
      setStreaming(false);
    }
  };

  const liveWordCount = streamText ? String(streamText).split(/\s+/).filter(Boolean).length : 0;

  return (
    <li
      className="rounded-md border p-3"
      style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}
      data-testid={`nf-chapter-${c.number}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em]"
          style={{ backgroundColor: P.oxblood, color: P.paper }}
        >
          {c.act || 'Act I'}
        </span>
        <span className="text-[11px] font-semibold" style={{ color: P.inkSoft }}>Ch {c.number}</span>
        <input
          value={c.title || ''}
          onChange={(e) => onPatch({ title: e.target.value })}
          data-testid={`nf-chapter-title-${c.number}`}
          placeholder="Chapter title"
          className="flex-1 rounded-sm border bg-transparent px-2 py-1 text-[13.5px] font-semibold outline-none"
          style={inputStyle()}
        />
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <select
          value={c.pov_character || ''}
          onChange={(e) => onPatch({ pov_character: e.target.value || null })}
          data-testid={`nf-chapter-pov-${c.number}`}
          className="rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
          style={inputStyle()}
        >
          <option value="">POV — pick a character</option>
          {characters.map((ch) => <option key={ch.id} value={ch.id}>{ch.name || '(unnamed)'} · {ch.role}</option>)}
        </select>
        <input
          value={c.setting || ''}
          onChange={(e) => onPatch({ setting: e.target.value })}
          data-testid={`nf-chapter-setting-${c.number}`}
          placeholder={locations.length ? 'Setting note (or pick location)' : 'Setting'}
          className="rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
          style={inputStyle()}
        />
        <input
          value={c.conflict || ''}
          onChange={(e) => onPatch({ conflict: e.target.value })}
          data-testid={`nf-chapter-conflict-${c.number}`}
          placeholder="Central conflict"
          className="rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
          style={inputStyle()}
        />
      </div>
      {locations.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <MapPin className="h-3 w-3 shrink-0" style={{ color: P.oxblood }} />
          <select
            value={c.location_id || ''}
            onChange={(e) => onPatch({ location_id: e.target.value || null })}
            data-testid={`nf-chapter-location-${c.number}`}
            className="flex-1 rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
            style={inputStyle()}
          >
            <option value="">Location — (none)</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name || '(unnamed)'}{l.kind ? ` · ${l.kind}` : ''}{l.mood ? ` — ${l.mood}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="mt-2">
        <input
          value={c.revelation || ''}
          onChange={(e) => onPatch({ revelation: e.target.value })}
          data-testid={`nf-chapter-revelation-${c.number}`}
          placeholder="Beat / revelation that closes the chapter (optional)"
          className="w-full rounded-sm border bg-transparent px-2 py-1 text-[12px] outline-none"
          style={inputStyle()}
        />
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold" style={{ color: P.inkSoft }}>
            Transition · scene brief (150–500 words)
          </span>
          <span className="text-[10.5px]" style={{ color: wcStatus.color }}>{wcStatus.label}</span>
        </div>
        <textarea
          value={c.transition || ''}
          onChange={(e) => onPatch({ transition: e.target.value })}
          data-testid={`nf-chapter-transition-${c.number}`}
          rows={5}
          placeholder="Where the chapter opens, what the POV character wants in this scene, what escalates, what changes by the end."
          className="mt-1 w-full resize-y rounded-sm border p-2 text-[12.5px] outline-none"
          style={{ ...inputStyle(), fontFamily: 'Georgia, serif', minHeight: '110px' }}
        />
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          onClick={askSuggest}
          disabled={loadingSug || !c.transition}
          data-testid={`nf-chapter-suggest-${c.number}`}
          className="inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] font-semibold disabled:opacity-40"
          style={{ borderColor: `${P.oxblood}88`, color: P.oxblood }}
        >
          {loadingSug
            ? <><Loader2 className="h-3 w-3 animate-spin" /> Critiquing…</>
            : <><Wand2 className="h-3 w-3" /> Ask Claude to critique</>}
        </button>
      </div>
      {/* iter55g19 — Manuscript-derived hints. Only render when analyzer
       *  data is present so the row stays clean for un-linked frameworks. */}
      {(inferredBeat || peakingArcs.length > 0 || handOffNote) && (
        <div className="mt-2 space-y-1.5 rounded-sm border border-dashed p-2 text-[11.5px]"
             style={{ borderColor: `${P.gold}88`, backgroundColor: `${P.gold}0e`, color: P.ink }}
             data-testid={`nf-chapter-hints-${c.number}`}>
          {inferredBeat && (
            <div className="flex items-start gap-2" data-testid={`nf-chapter-beat-${c.number}`}>
              <Sparkles className="mt-0.5 h-3 w-3 shrink-0" style={{ color: P.oxblood }} />
              <div className="flex-1">
                <div className="font-semibold" style={{ color: P.oxblood }}>Inferred beat</div>
                <div className="italic">{inferredBeat}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  <button
                    onClick={() => onApplyBeat?.(inferredBeat)}
                    data-testid={`nf-chapter-apply-beat-${c.number}`}
                    className="rounded-sm px-2 py-0.5 text-[10.5px] font-semibold text-white"
                    style={{ backgroundColor: P.oxblood }}
                  >
                    Apply to transition
                  </button>
                  <button
                    onClick={onDismissBeat}
                    data-testid={`nf-chapter-dismiss-beat-${c.number}`}
                    className="rounded-sm border px-2 py-0.5 text-[10.5px] font-semibold"
                    style={{ borderColor: `${P.oxblood}66`, color: P.oxblood }}
                  >
                    Dismiss
                  </button>
                  {/* iter55g20 — one-click Draft-from-beat with live streaming.
                      Group-A: button now opens the pre-flight dialog first. */}
                  <button
                    onClick={() => setPreflightOpen(true)}
                    disabled={streaming || !projectId}
                    data-testid={`nf-chapter-draft-beat-${c.number}`}
                    title={projectId
                      ? `Apply this beat and draft Chapter ${c.number} now`
                      : 'Link this framework to a project to enable drafting.'}
                    className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] font-semibold disabled:opacity-40"
                    style={{ borderColor: `${P.gold}dd`, color: P.oxblood, backgroundColor: `${P.gold}22` }}
                  >
                    {streaming
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> Drafting…</>
                      : <><Zap className="h-3 w-3" /> Draft chapter from beat</>}
                  </button>
                </div>
              </div>
            </div>
          )}
          {peakingArcs.length > 0 && (
            <div className="flex items-start gap-2" data-testid={`nf-chapter-arcs-${c.number}`}>
              <Users className="mt-0.5 h-3 w-3 shrink-0" style={{ color: P.oxblood }} />
              <div>
                <b>Arc peaks here:</b>{' '}
                {peakingArcs.map((a, i) => (
                  <span key={`${a.name}-${i}`} className="italic">
                    {a.name}{a.arc ? ` — ${a.arc}` : ''}{i < peakingArcs.length - 1 ? '; ' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          {handOffNote && (
            <div className="flex items-start gap-2" data-testid={`nf-chapter-handoff-${c.number}`}>
              <ChevronRight className="mt-0.5 h-3 w-3 shrink-0" style={{ color: P.oxblood }} />
              <div>
                <b>Hands off to Ch {c.number + 1}:</b> <span className="italic">{handOffNote}</span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* iter55g20 — Live streaming preview of the Draft-from-beat flow. */}
      {(streaming || streamText || streamError) && (
        <div
          className="mt-2 rounded-sm border border-dashed p-2 text-[11.5px]"
          style={{ borderColor: `${P.oxblood}88`, backgroundColor: `${P.paperDeep}` }}
          data-testid={`nf-chapter-stream-${c.number}`}
        >
          <div className="mb-1 flex items-center gap-2 text-[11px]" style={{ color: P.oxblood }}>
            {streaming
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Zap className="h-3 w-3" />}
            <b>{streaming ? 'Drafting Chapter ' + c.number + '…' : 'Draft complete'}</b>
            {/* Group-A — cache status badge */}
            {!streaming && cacheStatus && (() => {
              const { read, write, savedUsd } = cacheStatus;
              const isHit   = read > 0;
              const isWrite = !isHit && write > 0;
              const isMiss  = !isHit && !isWrite;
              const badgeColor = isHit ? P.verdant : isWrite ? '#3b6fca' : '#b87200';
              const label = isHit ? 'Cache HIT' : isWrite ? 'Cache WRITE' : 'Cache MISS';
              return (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                  style={{ backgroundColor: badgeColor }}
                  data-testid={`nf-chapter-cache-badge-${c.number}`}
                >
                  {label}{savedUsd > 0 ? ` · saved $${savedUsd.toFixed(4)}` : ''}
                </span>
              );
            })()}
            <span
              className="ml-auto font-mono-stat"
              data-testid={`nf-chapter-stream-count-${c.number}`}
              style={{ color: liveWordCount >= (targetWords ? Math.floor(targetWords / Math.max(1, 1)) : 1000) ? P.verdant : P.inkSoft }}
            >
              {liveWordCount.toLocaleString()} words
              {targetWords ? ` / ~${targetWords.toLocaleString()}` : ''}
            </span>
          </div>
          <pre
            className="max-h-40 overflow-auto whitespace-pre-wrap text-[11.5px] leading-relaxed"
            style={{ fontFamily: 'Georgia, serif', color: P.ink }}
          >
            {streamText || (streamError ? `Error: ${streamError}` : '(waiting for Claude…)')}
          </pre>
          {!streaming && streamText && (
            <div className="mt-1 text-[10.5px] italic" style={{ color: P.inkSoft }}>
              Full chapter is saved — reopen it in the Manuscript panel to keep editing.
            </div>
          )}
        </div>
      )}
      {suggestion && (
        <div
          className="mt-2 whitespace-pre-wrap rounded-sm border border-dashed p-3 text-[12px] leading-relaxed"
          style={{ borderColor: `${P.oxblood}66`, backgroundColor: `${P.oxblood}0d`, color: P.ink }}
          data-testid={`nf-chapter-critique-${c.number}`}
        >
          {suggestion}
        </div>
      )}
      {/* Group-A — pre-flight dialog rendered per-chapter (portals to body via fixed positioning) */}
      <DraftPreflightDialog
        open={preflightOpen}
        onClose={() => setPreflightOpen(false)}
        onProceed={(caching) => { setPreflightOpen(false); draftFromBeat(caching); }}
        cachingDefault={promptCachingDefault ?? true}
        lastDraftAt={lastDraftAt}
        chapterNum={c.number}
      />
    </li>
  );
}

function SectionAudit({ f }) {
  const [audit, setAudit]   = useState('');
  const [loading, setLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);   // iter-audit1 — Prompt Audit drawer
  const run = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/framework/${f.id}/audit`, {});
      setAudit(r.data.audit || '');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Audit failed.'));
    } finally { setLoading(false); }
  };
  return (
    <SectionCard
      title="Roadmap to conclusion"
      subtitle="A developmental audit. Claude reads the whole framework and returns Beginning · Middle · End notes plus the highest-impact next steps."
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={run}
          disabled={loading}
          data-testid="nf-audit-run"
          className="inline-flex items-center gap-2 rounded-sm px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: P.oxblood }}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {audit ? 'Re-run audit' : 'Run audit'}
        </button>
        {/* iter-audit1 — dry-run prompt preview */}
        <button
          onClick={() => setAuditOpen(true)}
          data-testid="nf-prompt-audit-open"
          className="inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-[12.5px] font-semibold hover:bg-primary/10"
          style={{ borderColor: `${P.oxblood}77`, color: P.oxblood }}
          title="Preview the exact prompt Claude receives — no tokens spent"
        >
          <FileText className="h-4 w-4" /> Preview Claude prompt
        </button>
      </div>
      {audit && (
        <pre
          className="mt-4 whitespace-pre-wrap rounded-sm border p-4 text-[13px] leading-relaxed"
          style={{ borderColor: `${P.gold}88`, backgroundColor: P.paper,
                    fontFamily: 'Georgia, serif', color: P.ink }}
          data-testid="nf-audit-output"
        >
          {audit}
        </pre>
      )}
      {auditOpen && (
        <PromptAuditDrawer
          framework={f}
          defaultChapter={(f.chapters?.[0]?.number) || 1}
          onClose={() => setAuditOpen(false)}
        />
      )}
      {/* iter-studio-dashboard — Prompt Quality checklist inline
          next to the audit tools so writers see which bible/framework
          fields they most often leave blank while they're editing. */}
      <div className="mt-4">
        <PromptQualityChecklist days={30} testId="nf-prompt-quality" />
      </div>
    </SectionCard>
  );
}

function SectionImport({ f, onImported }) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/framework/${f.id}/import`, {});
      setResult(r.data);
      toast.success('Project created — drafting chapters 1–3 in the background.');
      setTimeout(() => onImported(r.data.project_id), 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Import failed.'));
    } finally { setImporting(false); }
  };

  const missingChars = (f.characters || []).length === 0;
  const shortTransitions = (f.chapters || []).filter((c) => (c.transition_words ?? 0) > 0 && (c.transition_words ?? 0) < 150).length;
  const emptyChapters = (f.chapters || []).filter((c) => !(c.transition || '').trim()).length;

  return (
    <SectionCard
      title="Import into The Loom"
      subtitle="Creates a new project seeded with your bible, characters, and chapter beats — then automatically drafts chapters 1–3. Chapter 4+ stays on-demand from the editor."
    >
      {(missingChars || shortTransitions > 0 || emptyChapters > 0) && (
        <div
          className="rounded-sm border border-dashed p-3 text-[12px]"
          style={{ borderColor: `${P.oxblood}88`, backgroundColor: `${P.oxblood}0d`, color: P.oxblood }}
        >
          <div className="font-semibold">Ready to import — a few warnings:</div>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {missingChars       && <li>No characters defined. Claude will invent them if you continue.</li>}
            {shortTransitions   > 0 && <li>{shortTransitions} chapter(s) have transitions under 150 words. The auto-draft may drift from your intent.</li>}
            {emptyChapters      > 0 && <li>{emptyChapters} chapter(s) have empty transitions. Claude will invent the beats.</li>}
          </ul>
        </div>
      )}

      <div className="rounded-sm border p-3 text-[12.5px]"
           style={{ borderColor: `${P.gold}88`, backgroundColor: P.paperDeep, color: P.ink }}>
        <div className="font-semibold" style={{ color: P.oxblood }}>What will happen</div>
        <ol className="mt-1 list-inside list-decimal space-y-0.5">
          <li>A new The Loom project is created with {f.chapter_count} chapter shells + your {(f.characters || []).length} character(s).</li>
          <li>Story bible, POV/tense/voice, and toggles are copied across.</li>
          <li>Chapters 1–3 begin drafting immediately in the background. You'll see them fill in as you edit.</li>
          <li>Chapter 4+ waits until you press "Draft this chapter" in the editor.</li>
        </ol>
      </div>

      <button
        onClick={run}
        disabled={importing || !!result || !!f.imported_project_id}
        data-testid="nf-import-run"
        className="inline-flex items-center gap-2 rounded-sm px-5 py-2.5 text-[14px] font-semibold text-white disabled:opacity-50"
        style={{ backgroundColor: P.oxblood }}
      >
        {importing
          ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating project…</>
          : f.imported_project_id
            ? <>Already imported ✓</>
            : <><Rocket className="h-4 w-4" /> Import to The Loom</>}
      </button>

      {result && (
        <div
          className="rounded-sm border p-3 text-[12.5px]"
          style={{ borderColor: `${P.verdant}88`, backgroundColor: `${P.verdant}11`, color: P.ink }}
          data-testid="nf-import-result"
        >
          <div className="font-semibold" style={{ color: P.verdant }}>
            Project created: {result.project_id}
          </div>
          <div className="mt-0.5">
            Auto-drafting chapters: {result.auto_drafting_chapters?.join(', ') || '—'} · seeded {result.chapters_seeded} chapters · {result.characters_seeded} characters.
          </div>
          <div className="mt-1 text-[11px]" style={{ color: P.inkSoft }}>
            Redirecting to the workspace…
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function countWords(text) {
  if (!text) return 0;
  const m = text.match(/\b\w+\b/g);
  return m ? m.length : 0;
}
